import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const VarietyEditorDialog = ({
  isOpen = true,
  eyebrow = 'Variedad',
  title,
  description,
  headerActions,
  children,
  footerNote,
  footerActions,
  onClose,
  busy = false,
  escapeDisabled = false,
  closeLabel = 'Cerrar editor de variedad',
  panelClassName = '',
  placement = 'center',
}) => {
  const generatedId = useId();
  const titleId = `variety-editor-title-${generatedId.replace(/:/g, '')}`;
  const descriptionId = `variety-editor-description-${generatedId.replace(/:/g, '')}`;
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  const escapeDisabledRef = useRef(escapeDisabled);
  onCloseRef.current = onClose;
  busyRef.current = busy;
  escapeDisabledRef.current = escapeDisabled;

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => closeButtonRef.current?.focus(), 0);
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (!busyRef.current && !escapeDisabledRef.current) onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter((element) => element.getAttribute('aria-hidden') !== 'true');
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') previouslyFocused.focus();
    };
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[2000] flex items-stretch bg-slate-950/60 p-0 backdrop-blur-[1px] ${placement === 'right' ? 'justify-end' : 'justify-center sm:items-start sm:p-4'}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose?.();
      }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        aria-busy={busy}
        className={`flex h-[100dvh] w-full flex-col overflow-hidden bg-white shadow-2xl ${placement === 'right' ? 'max-w-xl border-l border-gray-100' : 'max-w-7xl sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl sm:border sm:border-gray-100'} ${panelClassName}`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header
          className="relative flex shrink-0 flex-col gap-2 border-b border-gray-200 bg-slate-50 px-4 pb-3 sm:flex-row sm:items-start sm:justify-between sm:gap-3 sm:px-6 sm:pb-4"
          style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }}
        >
          <div className="min-w-0 flex-1 pr-12 sm:pr-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-blue-700">{eyebrow}</p>
            <h2 id={titleId} className="mt-0.5 break-words text-lg font-semibold leading-tight text-gray-950 sm:text-xl">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="mt-1 max-h-10 max-w-4xl overflow-hidden text-xs leading-relaxed text-gray-500 sm:max-h-none sm:text-sm">
                {description}
              </p>
            )}
          </div>
          <div className="flex w-full flex-none flex-wrap items-center gap-2 pr-12 sm:w-auto sm:max-w-[55%] sm:justify-end sm:pr-0">
            {headerActions}
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              disabled={busy}
              aria-label={closeLabel}
              className="absolute right-4 top-4 flex h-10 w-10 flex-none items-center justify-center rounded-full border border-gray-200 bg-white text-lg text-gray-600 shadow-sm hover:bg-gray-50 hover:text-gray-950 disabled:cursor-not-allowed disabled:opacity-50 sm:static"
              style={{ top: 'calc(1rem + env(safe-area-inset-top))' }}
            >
              <span aria-hidden="true">✕</span>
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 sm:px-4 sm:py-4">
          {children}
        </div>

        <footer
          className="shrink-0 border-t border-gray-200 bg-slate-50 px-4 pt-3 sm:px-6 sm:py-4"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 text-xs leading-relaxed text-gray-500">{footerNote}</div>
            <div className="grid w-full grid-cols-2 items-stretch gap-2 [&>*]:min-w-0 sm:flex sm:w-auto sm:flex-wrap sm:items-center sm:justify-end">{footerActions}</div>
          </div>
        </footer>
      </section>
    </div>,
    document.body
  );
};

export default VarietyEditorDialog;
